import { useCallback, useRef } from 'react';
import { applyNodeChanges, NodeChange, NodePositionChange, XYPosition } from '@xyflow/react';
import { RfNodeReorderNode, SweepCanvasRfNode } from '../canvas-types/nodeTypesData';
import { CanvasElementType } from '../canvas-types/canvasTypes';
import { useSweepCanvasState } from '../internal-context/CanvasStateContext';
import {
  createObjectOperations,
  ObjectOperations,
} from '../node-changes-event-handlers/objectOperations';
import { snapPositionToGrid } from '../helpers/gridPositioningUtils';

export const useOnReorderStepsNodesChange = () => {
  const { setCanvasNodes, getCanvasNodes } = useSweepCanvasState();

  const currentNodeLayoutRef = useRef<ObjectOperations<RfNodeReorderNode> | null>(null);

  const onReorderStepsNodesChange = useCallback(
    (nodeChanges: NodeChange<SweepCanvasRfNode>[]) => {
      const positionTransformations: NodePositionChange[] = nodeChanges.filter(
        (nodeChange) => nodeChange.type === 'position',
      );

      if (positionTransformations.length === 0) {
        setCanvasNodes((nodes) => {
          const appliedNodes = applyNodeChanges(nodeChanges, nodes);
          return appliedNodes;
        });
        return;
      }
      if (positionTransformations.length > 1) {
        throw new Error('There should be only one position transformation');
      }

      const nodes = getCanvasNodes();
      const {
        setObject: setNode,
        getObjects: getNodes,
        setObjects: setNodes,
      } = createObjectOperations(nodes);

      const nodePositionChange = positionTransformations[0];
      const { id: movingNodeId, position: newNodePosition } = nodePositionChange;
      if (!newNodePosition) {
        throw new Error('The position is not set');
      }

      const findReorderNodeInPosition = (position: XYPosition) => {
        return currentNodeLayoutRef.current
          ?.getObjects()
          .find(
            (node) =>
              node.position.x === position.x &&
              node.position.y === position.y &&
              node.type === CanvasElementType.REORDER_NODE &&
              node.draggable,
          ) as RfNodeReorderNode | undefined;
      };

      const resetNodesToBeforeDraggingPosition = () => {
        currentNodeLayoutRef.current?.getObjects().forEach((originalNodePosition) => {
          setNode(originalNodePosition.id, (node) => ({
            ...node,
            position: originalNodePosition.position,
          }));
        });
      };

      const handleFirstTimeDragging = () => {
        if (currentNodeLayoutRef.current) {
          throw new Error('The beforeDraggingNodePositionRef is already set');
        }
        currentNodeLayoutRef.current = createObjectOperations<RfNodeReorderNode>(
          nodes.filter(
            ({ type }) => type === CanvasElementType.REORDER_NODE,
          ) as RfNodeReorderNode[],
        );
      };

      const handleSubsequentDragging = () => {
        // If the beforeDraggingNodePositionRef is set, we need to move the node to the new position
        // This is not the first time the node is being dragged

        if (!currentNodeLayoutRef.current) {
          throw new Error('The beforeDraggingNodePositionRef is not set');
        }

        const dropZoneGridPosition = snapPositionToGrid(newNodePosition);
        const nodeInPosition = findReorderNodeInPosition(dropZoneGridPosition);

        const movingNodeBeforeDragging = currentNodeLayoutRef.current.getObject(movingNodeId);
        if (!movingNodeBeforeDragging) {
          throw new Error('The node is not found');
        }
        resetNodesToBeforeDraggingPosition();
        if (nodeInPosition && nodeInPosition?.id !== movingNodeId) {
          setNode(nodeInPosition.id, (node) => ({
            ...node,
            position: movingNodeBeforeDragging.position,
          }));
        } else {
        }
      };

      const handleDropping = () => {
        // If it's not dragging, we need to move the node to the original position

        resetNodesToBeforeDraggingPosition();
        const originalMovingNodePosition =
          currentNodeLayoutRef.current?.getObject(movingNodeId)?.position;
        if (!originalMovingNodePosition) {
          throw new Error('The original moving node position is not found');
        }
        setNodes(applyNodeChanges(nodeChanges, getNodes()));
        const nodeInPosition = findReorderNodeInPosition(snapPositionToGrid(newNodePosition));

        if (nodeInPosition) {
          setNode(nodeInPosition.id, (node) => ({
            ...node,
            position: originalMovingNodePosition,
          }));
          setNode(movingNodeId, (node) => ({
            ...node,
            position: nodeInPosition.position,
          }));
        } else {
          setNode(movingNodeId, (node) => ({
            ...node,
            position: originalMovingNodePosition,
          }));
        }
        setCanvasNodes(getNodes());

        currentNodeLayoutRef.current = null;
      };

      if (nodePositionChange.dragging) {
        currentNodeLayoutRef.current ? handleSubsequentDragging() : handleFirstTimeDragging();

        setCanvasNodes(applyNodeChanges(nodeChanges, getNodes()));
        return;
      }
      if (!nodePositionChange.dragging) {
        handleDropping();
      }
    },
    [getCanvasNodes, currentNodeLayoutRef, setCanvasNodes],
  );

  return { onReorderStepsNodesChange };
};
